import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import { EnvelopeSimple, Lock, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LoginPage = () => {
  const { login, t, language } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await login(email, password);
      toast.success(language === 'de' ? 'Erfolgreich angemeldet!' : 'Successfully logged in!');
      // Role-aware default landing page (only when no explicit `from` was provided)
      const explicitFrom = location.state?.from?.pathname;
      const next = new URLSearchParams(location.search).get('next');
      const fallback =
        data?.role === 'trainer' ? '/trainer/schedule'
        : data?.role === 'admin' ? '/admin'
        : data?.role === 'staff' ? '/admin/bookings'
        : '/book';
      navigate(next || explicitFrom || fallback, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = language === 'de' ? 'Anmeldung fehlgeschlagen' : 'Login failed';
      if (typeof detail === 'string') errorMsg = detail;
      else if (Array.isArray(detail)) errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 bg-[#F8FAFC]">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="card-static p-8 md:p-10" data-testid="login-form">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="Padelarena Wädenswil" className="h-20 w-auto mx-auto mb-4" />
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 tracking-tight">Padelarena Wädenswil</h1>
            <p className="text-[10px] text-emerald-600 font-bold tracking-wider uppercase mb-3">Padlr. Booking</p>
            <h2 className="text-lg font-semibold text-slate-700 mb-1">{t('loginTitle')}</h2>
            <p className="text-slate-500 text-sm">{language === 'de' ? 'Melden Sie sich bei Ihrem Konto an' : 'Sign in to your account'}</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-text">{t('email')}</Label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="email" type="text" inputMode="email" autoCapitalize="off" autoCorrect="off" spellCheck={false} autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 rounded-lg"
                  placeholder="name@example.com" required data-testid="login-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-text">{t('password')}</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 rounded-lg"
                  placeholder="********" required data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="text-right">
                <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-emerald-600 font-medium" data-testid="forgot-password-link">
                  {language === 'de' ? 'Passwort vergessen?' : 'Forgot password?'}
                </Link>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full btn-primary disabled:opacity-50" data-testid="login-submit">
              {loading ? (<span className="flex items-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>{t('loading')}</span>) : t('login')}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm">{t('noAccount')}{' '}<Link to="/register" className="text-emerald-600 hover:underline font-medium" data-testid="register-link">{t('register')}</Link></p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
